import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useRouter } from 'shared-routing';
import { paymentOptions, Routes } from '@common/constants';
import { analytics, formatBasketItems, formatPrice } from '@features/analytics';
import { createOrder } from '@store/basket/actions';
import { basketSelectors } from '@store/basket/selectors';

export function useStartCheckout() {
  const router = useRouter();
  const dispatch = useDispatch();
  const isSinglePageCheckout = useFeatureIsOn('feature.single-page-checkout');
  const basketDetails = useSelector(basketSelectors.getBasketDetails) as any;
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);

  const startCheckout = useCallback(async () => {
    if (isStartingCheckout || !basketDetails) return;

    analytics.track({
      event: 'begin_checkout',
      params: {
        currency: basketDetails.total?.currencyCode,
        value: formatPrice(basketDetails.total?.amount),
        items: formatBasketItems(basketDetails.items || []),
        coupon: basketDetails.promoCode || undefined,
      },
    });

    if (!isSinglePageCheckout) {
      router.push({ pathname: Routes.CHECKOUT });
      return;
    }

    const hasExpiredItems = basketDetails.items?.some(
      (item: { expireAt?: string }) => item.expireAt && Date.now() > Date.parse(item.expireAt)
    );
    if (hasExpiredItems) {
      router.push({ pathname: Routes.BASKET });
      return;
    }

    setIsStartingCheckout(true);
    try {
      const result = (await (dispatch as (action: unknown) => Promise<unknown>)(
        createOrder({
          basket: { id: basketDetails.id },
          agreedToMarketingEmail: false,
          agreedToMarketingSms: false,
          paymentOption: basketDetails.fullPaymentAllowed
            ? paymentOptions.FULL
            : paymentOptions.DEFERRED,
          skipCompletion: true,
        })
      )) as { orderReference?: string } | undefined;

      if (result?.orderReference) {
        router.push({ pathname: Routes.CHECKOUT });
      } else {
        router.push({ pathname: Routes.BASKET });
      }
    } finally {
      setIsStartingCheckout(false);
    }
  }, [isStartingCheckout, basketDetails, isSinglePageCheckout, dispatch, router]);

  return { startCheckout, isStartingCheckout };
}
